import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiClient } from 'Utils/api';
import Header from 'View/Component/Header/Header';
import Footer from 'View/Component/Footer/Footer';
import * as S from './CheckoutConfirmationPage.style';

interface OrderItem {
  watchId: string;
  slug: string;
  brand: string;
  model: string;
  referenceNumber?: string;
  shortCode?: string | null;
}

const CheckoutConfirmationPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [orderInfo, setOrderInfo] = useState<{
    id: string;
    orderNumber: string;
    deliveryMethod?: 'SHIPPING' | 'PICKUP';
    paymentMethod?: string;
  } | null>(null);
  const [items, setItems] = useState<OrderItem[]>([]);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const [withdrawnAt, setWithdrawnAt] = useState<string | null>(null);
  const [withdrawing, setWithdrawing] = useState(false);
  const [showWithdrawConfirm, setShowWithdrawConfirm] = useState(false);

  const isPickup = orderInfo?.deliveryMethod === 'PICKUP';
  const isOnsitePayment = orderInfo?.paymentMethod === 'PICKUP_ONSITE';

  useEffect(() => {
    const savedOrder = sessionStorage.getItem('lastOrder');
    const queryId = new URLSearchParams(window.location.search).get('order');

    if (savedOrder) {
      const parsed = JSON.parse(savedOrder);
      setOrderInfo(parsed);
      loadPublicOrder(parsed.id);
      return;
    }

    if (queryId) {
      loadPublicOrder(queryId, true);
      return;
    }

    navigate('/');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const loadPublicOrder = async (orderId: string, fromQuery = false) => {
    try {
      const res = await apiClient.get(`/api/orders/${orderId}/public`);
      if (res.data?.status === 'success' && res.data.order) {
        const o = res.data.order;
        setItems(o.items || []);
        setWithdrawnAt(o.withdrawalRequestedAt || null);
        if (fromQuery) {
          setOrderInfo({
            id: o.id,
            orderNumber: o.orderNumber,
            deliveryMethod: o.deliveryMethod,
            paymentMethod: o.paymentMethod,
          });
        }
      } else if (fromQuery) {
        navigate('/');
      }
    } catch (e) {
      if (fromQuery) navigate('/');
    }
  };

  const handleWithdraw = async () => {
    if (!orderInfo || withdrawing) return;
    setWithdrawing(true);
    try {
      const res = await apiClient.post(`/api/orders/${orderInfo.id}/withdraw`);
      if (res.data?.status === 'success') {
        setWithdrawnAt(res.data.withdrawalRequestedAt || new Date().toISOString());
        setShowWithdrawConfirm(false);
      }
    } catch (e) {
      /* keep button available on failure */
    } finally {
      setWithdrawing(false);
    }
  };

  const buildShortLinkUrl = (code: string) => `${window.location.origin}/s/${code}`;

  const handleCopy = async (code: string) => {
    try {
      await navigator.clipboard.writeText(buildShortLinkUrl(code));
      setCopiedCode(code);
      setTimeout(() => setCopiedCode((current) => (current === code ? null : current)), 2500);
    } catch {
      /* clipboard unavailable */
    }
  };

  if (!orderInfo) {
    return null;
  }

  return (
    <>
      <Header />
      <S.PageContainer>
        <S.SuccessIcon>✓</S.SuccessIcon>
        <S.Title>{t('confirmation.title')}</S.Title>
        <S.OrderNumber>{t('confirmation.order_number', { number: orderInfo.orderNumber })}</S.OrderNumber>

        <S.Message>
          <p>{t('confirmation.thanks')}</p>
          <p>{isPickup ? t('confirmation.email_info_pickup') : t('confirmation.email_info')}</p>
        </S.Message>

        {items.length > 0 && items.some((i) => i.shortCode) && (
          <div
            data-testid="confirmation-shortlinks"
            style={{
              maxWidth: 640,
              margin: '0 auto 2rem',
              padding: '1.25rem 1.5rem',
              border: '1px solid #e6e3da',
              borderRadius: 8,
              background: '#fcfaf4',
              textAlign: 'left',
              fontFamily: 'Abel',
            }}
          >
            <div style={{ fontWeight: 600, marginBottom: 8, color: '#212121', letterSpacing: '0.04em' }}>
              {t('shortlink.label')}
            </div>
            <div style={{ color: '#657455', fontSize: '0.9rem', marginBottom: 12 }}>
              {t('shortlink.hint')}
            </div>
            {items.map((item) =>
              item.shortCode ? (
                <div
                  key={item.watchId}
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 12,
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '0.6rem 0',
                    borderTop: '1px solid #f0ece2',
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <div style={{ color: '#212121', fontWeight: 500 }}>
                      {item.brand} {item.model}
                    </div>
                    <a
                      href={buildShortLinkUrl(item.shortCode)}
                      style={{
                        color: '#b8964a',
                        fontFamily: 'monospace',
                        fontSize: '0.95rem',
                        wordBreak: 'break-all',
                      }}
                      data-testid={`shortlink-${item.shortCode}`}
                    >
                      {buildShortLinkUrl(item.shortCode)}
                    </a>
                  </div>
                  <button
                    type="button"
                    onClick={() => item.shortCode && handleCopy(item.shortCode)}
                    style={{
                      background: copiedCode === item.shortCode ? '#5b7a3d' : '#212121',
                      color: '#fff',
                      border: 'none',
                      padding: '0.5rem 1rem',
                      borderRadius: 4,
                      cursor: 'pointer',
                      fontFamily: 'Abel',
                      fontSize: '0.85rem',
                      letterSpacing: '0.06em',
                    }}
                    data-testid={`shortlink-copy-${item.shortCode}`}
                  >
                    {copiedCode === item.shortCode ? t('shortlink.copied') : t('shortlink.copy')}
                  </button>
                </div>
              ) : null,
            )}
          </div>
        )}

        <S.InfoBox>
          <S.InfoTitle>{t('confirmation.next_steps_title')}</S.InfoTitle>
          <S.InfoList>
            {isPickup ? (
              <>
                <li>{t('confirmation.pickup_step1')}</li>
                <li>{t('confirmation.pickup_step2')}</li>
                <li>
                  {isOnsitePayment
                    ? t('confirmation.pickup_step3_onsite')
                    : t('confirmation.pickup_step3_sepa')}
                </li>
                <li>{t('confirmation.pickup_step4')}</li>
              </>
            ) : (
              <>
                <li>{t('confirmation.step1')}</li>
                <li>{t('confirmation.step2')}</li>
                <li>{t('confirmation.step3')}</li>
                <li>{t('confirmation.step4')}</li>
              </>
            )}
          </S.InfoList>
        </S.InfoBox>

        <div
          data-testid="confirmation-withdrawal"
          style={{
            maxWidth: 640,
            margin: '0 auto 2rem',
            padding: '1.5rem',
            border: '2px solid #b8964a',
            borderRadius: 8,
            background: '#fffdf6',
            textAlign: 'left',
            fontFamily: 'Abel',
          }}
        >
          <div style={{ fontWeight: 700, fontSize: '1.1rem', marginBottom: 8, color: '#212121', letterSpacing: '0.04em' }}>
            {t('withdrawal.title', { defaultValue: 'Ihr Widerrufsrecht' })}
          </div>
          <p style={{ color: '#4a4a45', fontSize: '0.92rem', lineHeight: 1.5, margin: '0 0 12px' }}>
            {t('withdrawal.notice', {
              defaultValue:
                'Sie haben das Recht, binnen vierzehn Tagen ohne Angabe von Gründen diesen Vertrag zu widerrufen. Die vollständige Widerrufsbelehrung und das Muster-Widerrufsformular finden Sie hier:',
            })}{' '}
            <a href="/widerruf" target="_blank" rel="noopener noreferrer" style={{ color: '#b8964a', fontWeight: 600 }} data-testid="withdrawal-policy-link">
              {t('withdrawal.policy_link', { defaultValue: 'Widerrufsbelehrung & Formular' })}
            </a>
          </p>

          {withdrawnAt ? (
            <div
              data-testid="withdrawal-confirmed"
              style={{ background: '#eef3e7', border: '1px solid #5b7a3d', borderRadius: 6, padding: '0.9rem 1rem', color: '#3f5a2a', fontSize: '0.92rem' }}
            >
              ✓ {t('withdrawal.confirmed', {
                defaultValue:
                  'Ihr Widerruf wurde erfasst. Sie erhalten eine Bestätigung per E-Mail; wir setzen uns zur Rückabwicklung mit Ihnen in Verbindung.',
              })}
            </div>
          ) : !showWithdrawConfirm ? (
            <button
              type="button"
              onClick={() => setShowWithdrawConfirm(true)}
              data-testid="withdrawal-declare-button"
              style={{
                background: '#212121',
                color: '#fff',
                border: 'none',
                padding: '0.7rem 1.4rem',
                borderRadius: 4,
                cursor: 'pointer',
                fontFamily: 'Abel',
                fontSize: '0.9rem',
                letterSpacing: '0.06em',
              }}
            >
              {t('withdrawal.declare_button', { defaultValue: 'Diese Bestellung widerrufen' })}
            </button>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ color: '#4a4a45', fontSize: '0.9rem' }}>
                {t('withdrawal.confirm_question', { defaultValue: 'Möchten Sie diese Bestellung wirklich widerrufen?' })}
              </div>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <button
                  type="button"
                  onClick={handleWithdraw}
                  disabled={withdrawing}
                  data-testid="withdrawal-confirm-button"
                  style={{
                    background: '#b8964a',
                    color: '#fff',
                    border: 'none',
                    padding: '0.7rem 1.4rem',
                    borderRadius: 4,
                    cursor: withdrawing ? 'not-allowed' : 'pointer',
                    fontFamily: 'Abel',
                    fontSize: '0.9rem',
                    letterSpacing: '0.06em',
                    opacity: withdrawing ? 0.7 : 1,
                  }}
                >
                  {withdrawing
                    ? t('withdrawal.submitting', { defaultValue: 'Wird gesendet…' })
                    : t('withdrawal.confirm_yes', { defaultValue: 'Ja, Widerruf erklären' })}
                </button>
                <button
                  type="button"
                  onClick={() => setShowWithdrawConfirm(false)}
                  disabled={withdrawing}
                  data-testid="withdrawal-cancel-button"
                  style={{
                    background: 'transparent',
                    color: '#212121',
                    border: '1px solid #ccc',
                    padding: '0.7rem 1.4rem',
                    borderRadius: 4,
                    cursor: 'pointer',
                    fontFamily: 'Abel',
                    fontSize: '0.9rem',
                  }}
                >
                  {t('withdrawal.confirm_no', { defaultValue: 'Abbrechen' })}
                </button>
              </div>
            </div>
          )}
        </div>

        <S.ButtonGroup>
          <S.HomeButton onClick={() => navigate('/')}>
            {t('confirmation.home_button')}
          </S.HomeButton>
          <S.ShopButton onClick={() => navigate('/kaufen')}>
            {t('confirmation.shop_button')}
          </S.ShopButton>
        </S.ButtonGroup>
      </S.PageContainer>
      <Footer />
    </>
  );
};

export default CheckoutConfirmationPage;
